import { errorNotification } from 'components/ToastNotifications';

import L10n from './L10n';
import { isZoomContext } from './zoomApp';

export const API_FAILURE = 'API Failure';
export const BAD_REQUEST = 'Bad Request';
export const NOT_FOUND = 'Not Found';
export const FORBIDDEN = 'Forbidden';
export const CONFLICT = 'Conflict';
export const UNAUTHORIZED = 'Unauthorized';
export const JSON_PARSE_ERROR = 'Json Parse Error';
export const SERVER_ERROR = 'Internal Server Error';

const LOGOUT_LIST = ['/api/v1/userprofile', '/api/v1/meetings/'];
const isUrlInLogoutList = (url: string) => {
  for (let ii = 0; ii < LOGOUT_LIST.length; ii += 1) {
    const uu = LOGOUT_LIST[ii];
    if (url.includes(uu)) {
      return true;
    }
  }
  return false;
};

// Standalone pages that should never be yanked away by an unrelated 401 —
// see the matching guard/comment in helpers/http.js for why this exists.
const NO_REDIRECT_ON_401_PATHS = ['/outlook-addin/login'];
const isOnNoRedirectPath = () =>
  NO_REDIRECT_ON_401_PATHS.some(path =>
    window.location.pathname.startsWith(path)
  );

const getHeaderValue = (
  headers: Headers,
  headerNames: string[]
): string | undefined => {
  for (let ii = 0; ii < headerNames.length; ii += 1) {
    const value = headers.get(headerNames[ii]);
    if (value) {
      return value;
    }
  }

  return undefined;
};

const getResponseHeadersObject = (headers: Headers): Record<string, string> =>
  Object.fromEntries(Array.from(headers.entries()));

const getBackendErrorCode = (payload: any): string | number | undefined =>
  payload?.error_code ||
  payload?.errorCode ||
  payload?.code ||
  payload?.detail_code ||
  payload?.detailCode;

const normalizeBackendErrorMessage = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (value && typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  return undefined;
};

const getBackendErrorMessage = (
  payload: any,
  errorMessageField?: string
): string | undefined => {
  const standardMessage = normalizeBackendErrorMessage(
    payload?.detail ?? payload?.message ?? payload?.error ?? payload?.title
  );
  if (standardMessage) {
    return standardMessage;
  }

  const fieldMessage = errorMessageField ? payload?.[errorMessageField] : null;
  return typeof fieldMessage === 'string' && fieldMessage.trim()
    ? fieldMessage
    : undefined;
};

interface IRequestMeta {
  method?: string;
  url?: string;
  endpoint?: string;
}

export interface IThunkApiLike {
  dispatch?: (action: { type: string }) => void;
  type?: string;
  typePrefix?: string;
}

interface IHandleResponseOptions extends RequestInit {
  ignoreErrorNotification?: boolean;
  /** Skips the error notification only for these HTTP statuses (e.g. `[404]` for a lookup whose
   * caller already handles a missing record); every other error still notifies. */
  ignoreErrorNotificationStatuses?: number[];
  /** Read a string validation message from this response field as a fallback. */
  errorMessageField?: string;
  /** Delegate 5xx feedback to the caller; client errors keep global feedback. */
  errorPresentation?: 'toast' | 'inline';
}

interface IApiError extends Error {
  status?: number;
  data?: any;
  url?: string;
  method?: string;
  responseHeaders?: Record<string, string>;
  requestId?: string;
  traceId?: string;
  correlationId?: string;
  errorCode?: string | number;
  backendMessage?: string;
}

async function handleResponse(
  response: Response,
  thunkApi: IThunkApiLike = {},
  options: IHandleResponseOptions = {},
  requestMeta: IRequestMeta = {}
): Promise<any> {
  let json: any = {};
  const { ignoreErrorNotification = false, ignoreErrorNotificationStatuses } =
    options;
  if (response.status === 401) {
    if (isUrlInLogoutList(response.url)) {
      thunkApi.dispatch?.({ type: 'LOGOUT' });
    }
    if (response.url.includes('/public/')) {
      // window.location.href = '/signup';
    } else {
      // remove the redux state item so that there isn't a flicker glitch
      // in firefox when the token is expired
      localStorage.removeItem('reduxState');
      thunkApi.dispatch?.({ type: 'LOGOUT' });

      if (!isOnNoRedirectPath()) {
        window.location.href = isZoomContext()
          ? '/zoom/signup?error=401'
          : '/signup?error=401';
      }
    }
    const e = new Error(UNAUTHORIZED) as IApiError;
    e.name = UNAUTHORIZED;
    e.status = response.status;
    throw e;
  } else if (response.ok) {
    if (response.status !== 204) {
      try {
        json = await response.json();
      } catch (e) {
        const err = new Error(String(e)) as IApiError;
        err.name = JSON_PARSE_ERROR;
        err.status = response.status;
        throw err;
      }
    }
    return json;
  } else {
    let statusText = API_FAILURE;
    if (response.status === 403) {
      statusText = FORBIDDEN;
    } else if (response.status === 400) {
      statusText = BAD_REQUEST;
    } else if (response.status === 404) {
      statusText = NOT_FOUND;
    } else if (response.status === 409) {
      statusText = CONFLICT;
    } else if (response.status === 500) {
      statusText = SERVER_ERROR;
    }
    const e = new Error(statusText) as IApiError;
    e.name = statusText;
    e.status = response.status;
    try {
      json = await response.json();
    } catch (err) {
      // do nothing
    } finally {
      e.data = json;
    }

    const responseHeaders = getResponseHeadersObject(response.headers);
    const backendMessage = getBackendErrorMessage(
      json,
      options.errorMessageField
    );
    const errorCode = getBackendErrorCode(json);
    const requestId = getHeaderValue(response.headers, [
      'x-request-id',
      'request-id',
      'x-amzn-requestid'
    ]);
    const traceId = getHeaderValue(response.headers, [
      'x-trace-id',
      'trace-id',
      'sentry-trace'
    ]);
    const correlationId = getHeaderValue(response.headers, [
      'x-correlation-id',
      'correlation-id'
    ]);

    e.url = response.url;
    e.method = requestMeta.method;
    e.responseHeaders = responseHeaders;
    e.requestId = requestId;
    e.traceId = traceId;
    e.correlationId = correlationId;
    e.errorCode = errorCode;
    e.backendMessage = backendMessage;

    if (
      !ignoreErrorNotification &&
      !ignoreErrorNotificationStatuses?.includes(response.status)
    ) {
      const message = backendMessage || L10n.globalErrorHandler.heading;
      errorNotification({
        presentation:
          response.status >= 500
            ? options.errorPresentation ?? 'toast'
            : 'toast',
        message,
        error: e,
        errorCode,
        api: {
          url: response.url,
          endpoint: requestMeta.endpoint || response.url,
          method: requestMeta.method,
          status: response.status,
          errorCode,
          backendMessage,
          responseBody: json,
          responseHeaders,
          requestId,
          traceId,
          correlationId
        },
        extraContext: {
          thunkActionType: thunkApi?.type,
          thunkActionName: thunkApi?.typePrefix
        },
        source: 'http2'
      });
    }
    throw e;
  }
}
// We want the zoom app to use bearer token , while we want our app.avoma to user credentials include, hence this logic in the file

export const get = async ({
  url,
  thunkApi,
  options
}: {
  url: string | URL;
  thunkApi?: IThunkApiLike;
  options?: IHandleResponseOptions;
}) => {
  const headers: Record<string, string> = {};

  const fetchOptions = {
    method: 'GET',
    headers,
    ...options
  };

  if (!fetchOptions.credentials) {
    fetchOptions.credentials = 'include';
  }
  const response = await fetch(url, fetchOptions);
  return handleResponse(response, thunkApi, options, {
    method: 'GET',
    url: String(url),
    endpoint: String(url)
  });
};

export const post = async ({
  url,
  body,
  thunkApi,
  options
}: {
  url: string | URL;
  body?: unknown;
  thunkApi?: IThunkApiLike;
  options?: IHandleResponseOptions;
}) => {
  const headers: Record<string, string> = {};

  const fetchOptions = {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...headers
    },
    body: body ? JSON.stringify(body) : '{}',
    ...options
  };

  if (!fetchOptions.credentials) {
    fetchOptions.credentials = 'include';
  }
  const response = await fetch(url, fetchOptions);
  return handleResponse(response, thunkApi, options, {
    method: 'POST',
    url: String(url),
    endpoint: String(url)
  });
};

export const deleteMethod = async ({
  url,
  body,
  thunkApi,
  options
}: {
  url: string | URL;
  body?: unknown;
  thunkApi?: IThunkApiLike;
  options?: IHandleResponseOptions;
}) => {
  const headers: Record<string, string> = {};

  const fetchOptions = {
    method: 'DELETE',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...headers
    },
    body: body ? JSON.stringify(body) : '{}',
    ...options
  };

  if (!fetchOptions.credentials) {
    fetchOptions.credentials = 'include';
  }
  const response = await fetch(url, fetchOptions);
  return handleResponse(response, thunkApi, options, {
    method: 'DELETE',
    url: String(url),
    endpoint: String(url)
  });
};

export const put = async ({
  url,
  body,
  thunkApi,
  options
}: {
  url: string | URL;
  body?: unknown;
  thunkApi?: IThunkApiLike;
  options?: IHandleResponseOptions;
}) => {
  const headers: Record<string, string> = {};

  const fetchOptions = {
    method: 'PUT',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...headers
    },
    body: body ? JSON.stringify(body) : '{}',
    ...options
  };

  if (!fetchOptions.credentials) {
    fetchOptions.credentials = 'include';
  }
  const response = await fetch(url, fetchOptions);
  return handleResponse(response, thunkApi, options, {
    method: 'PUT',
    url: String(url),
    endpoint: String(url)
  });
};

export const patch = async ({
  url,
  body,
  thunkApi,
  options
}: {
  url: string | URL;
  body?: unknown;
  thunkApi?: IThunkApiLike;
  options?: IHandleResponseOptions;
}) => {
  const headers: Record<string, string> = {};

  const fetchOptions = {
    method: 'PATCH',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...headers
    },
    body: body ? JSON.stringify(body) : '{}',
    ...options
  };

  if (!fetchOptions.credentials) {
    fetchOptions.credentials = 'include';
  }
  const response = await fetch(url, fetchOptions);
  return handleResponse(response, thunkApi, options, {
    method: 'PATCH',
    url: String(url),
    endpoint: String(url)
  });
};
